import { CheckIcon } from "@heroicons/react/solid";
import { find, values } from "lodash";
import React, { useCallback, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { useWallets } from "../../../../context/wallets";

interface Props {}

const ConfirmWalletStep: React.FC<Props> = () => {
  const location = useLocation();
  const history = useHistory();
  const { wallets, addWallet } = useWallets();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const wizardPageMatch = matchPath<{
    walletAddress: string;
    stepKey: string;
  }>(location.pathname, {
    path: "/workspace/wallets/verify/:walletAddress/:stepKey"
  });

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || !wizardPageMatch?.params.walletAddress) {
      return;
    }

    try {
      setIsSubmitting(true);

      if (
        !find(values(wallets), {
          address: wizardPageMatch.params.walletAddress
        })
      ) {
        await addWallet(wizardPageMatch.params.walletAddress);
      }

      window.scrollTo(0, 0);

      history.push(
        `/workspace/wallets/verify/${wizardPageMatch.params.walletAddress}/set-donation`
      );
    } catch (e) {
      setIsSubmitting(false);
    }
  }, [
    addWallet,
    history,
    isSubmitting,
    wallets,
    wizardPageMatch?.params.walletAddress
  ]);

  return (
    <>
      <p className="mb-4">
        You are about to verify a wallet that controls the hotspots listed here.
      </p>

      <p>This will unlock the following collaboration features:</p>

      <div className="my-10 grid grid-cols-1 gap-4">
        {walletVerificationFeatures.map(({ title, description }) => (
          <div className="flex" key={title}>
            <div className="h-7 flex items-center">
              <CheckIcon className="h-full text-green-500" />
            </div>

            <div className="pl-4">
              <h3 className="text-lg sm:text-xl font-semibold inline-flex mb-2">
                {title}
              </h3>
              <p className="text-sm sm:text-md">{description}</p>
            </div>
          </div>
        ))}
      </div>

      <p>
        When hotspot owners can collaborate to maximize their earnings and
        increase coverage, the sky is the limit.
      </p>

      <div className="flex-shrink-0 px-4 py-4 flex justify-end">
        <button
          onClick={handleSubmit}
          type="button"
          className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 flex items-center disabled:opacity-50 disabled:cursor-default"
          disabled={isSubmitting}
        >
          Continue
        </button>
      </div>
    </>
  );
};

export default ConfirmWalletStep;

//
// Utils
//

const walletVerificationFeatures = [
  {
    title: "Anonymous messaging",
    description:
      "Send and receive messages with other hotspot owners while remaining anonymous."
  },
  {
    title: "Maximize your earnings",
    description:
      "Join forces, to work with and learn from each other and continually improve the people’s network."
  }
];
